import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import Sidebar from "@/components/app/Sidebar";
import Dashboard from "@/views/Dashboard";
import Directory from "@/views/Directory";
import ServiceDetail from "@/views/ServiceDetail";
import LeadsPortal from "@/views/LeadsPortal";
import WalletView from "@/views/WalletView";
import Workspaces from "@/views/Workspaces";
import WorkspaceDetail from "@/views/WorkspaceDetail";
import Generator from "@/views/Generator";
import Prospecting from "@/views/Prospecting";

const NAV_LABELS = {
  dashboard: "Dashboard", directory: "Service Directory", workspaces: "My Workspaces",
  prospecting: "Prospecting Agent", generator: "Invoice Generator", leads: "Leads Portal", wallet: "Wallet & Billing",
};

export default function AppShell() {
  const { profile, loading } = useApp();
  const { view, slug, wid } = useParams();
  const navigate = useNavigate();
  const [mobileNav, setMobileNav] = useState(false);

  useEffect(() => {
    if (!loading && !profile) navigate("/");
  }, [loading, profile, navigate]);

  useEffect(() => {
    if (!loading && profile) {
      let intent = null;
      try { intent = localStorage.getItem("dondrae_intent"); } catch { /* ignore */ }
      if (intent) {
        try { localStorage.removeItem("dondrae_intent"); } catch { /* ignore */ }
        navigate(`/app/${intent}`);
      }
    }
  }, [loading, profile, navigate]);

  if (loading) {
    return (
      <div className="h-screen clean-bg flex items-center justify-center text-zinc-500">
        <div className="animate-pulse text-xl font-display">Loading DONDRÁE…</div>
      </div>
    );
  }
  if (!profile) return null;

  const currentView = slug ? "service" : wid ? "workspaces" : view || "dashboard";

  const renderView = () => {
    if (slug) return <ServiceDetail slug={slug} />;
    if (wid) return <WorkspaceDetail workflowId={wid} />;
    switch (currentView) {
      case "directory": return <Directory />;
      case "prospecting": return <Prospecting />;
      case "leads": return <LeadsPortal />;
      case "wallet": return <WalletView />;
      case "workspaces": return <Workspaces />;
      case "generator": return <Generator />;
      case "dashboard":
      default: return <Dashboard />;
    }
  };

  return (
    <div className="h-screen flex w-full clean-bg overflow-hidden">
      <Sidebar active={currentView} />

      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        {/* Mobile header */}
        <div className="md:hidden glass-nav border-b border-white/10 px-5 py-4 flex items-center justify-between z-40">
          <div className="text-xl font-extrabold font-display" onClick={() => navigate("/")}>
            DONDR<span className="text-primary-400">Á</span>E
          </div>
          <button onClick={() => setMobileNav(!mobileNav)} className="text-white">
            {mobileNav ? <X /> : <Menu />}
          </button>
        </div>
        {mobileNav && (
          <div className="md:hidden glass-nav border-b border-white/10 px-4 py-4 grid grid-cols-2 gap-2 z-40">
            {Object.keys(NAV_LABELS).map((k) => (
              <button key={k} onClick={() => { navigate(`/app/${k}`); setMobileNav(false); }} className="px-3 py-2.5 rounded-lg bg-white/5 text-zinc-300 text-sm font-medium text-left">
                {NAV_LABELS[k]}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-hidden">{renderView()}</div>
      </main>
    </div>
  );
}
